import axios from "axios";
import React, { useContext, useEffect, useReducer, useState } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import LoadingBox from "../components/LoadingBox";
import MessageBox from "../components/MessageBox";
import { Store } from "../Store";
import { getError } from "../utils";

const reducer = (state, action) => {
  switch (action.type) {
    case "FETCH_REQUEST":
      return { ...state, loading: true };
    case "FETCH_SUCCESS":
      return { ...state, loading: false };
    case "FETCH_FAIL":
      return { ...state, loading: false, error: action.payload };
    case "UPDATE_REQUEST":
      return { ...state, loadingUpdate: true };
    case "UPDATE_SUCCESS":
      return { ...state, loadingUpdate: false };
    case "UPDATE_FAIL":
      return { ...state, loadingUpdate: false };
    default:
      return state;
  }
};

export default function UserEditScreen() {
  const [{ loading, error, loadingUpdate }, dispatch] = useReducer(reducer, {
    loading: true,
    error: "",
  });

  const { state } = useContext(Store);
  const { userInfo } = state;

  const params = useParams();
  const { id: userId } = params;
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSeller, setIsSeller] = useState(false); // Using isSeller as Manager for now
  const [isWaiter, setIsWaiter] = useState(false);
  const [businessType, setBusinessType] = useState('User');

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: "FETCH_REQUEST" });
        const { data } = await axios.get(`/api/users/${userId}`, {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        });
        setName(data.name);
        setEmail(data.email);
        setIsAdmin(data.isAdmin);
        setIsSeller(data.isSeller);
        setIsWaiter(data.isWaiter || false);
        setBusinessType(data.businessType || 'User');
        dispatch({ type: "FETCH_SUCCESS" });
      } catch (err) {
        dispatch({
          type: "FETCH_FAIL",
          payload: getError(err),
        });
      }
    };
    fetchData();
  }, [userId, userInfo]);

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      dispatch({ type: "UPDATE_REQUEST" });
      await axios.put(
        `/api/users/${userId}`,
        { _id: userId, name, email, isAdmin, isSeller, isWaiter, businessType },
        {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        }
      );
      dispatch({
        type: "UPDATE_SUCCESS",
      });
      toast.success("User updated successfully");
      navigate("/admin/users");
    } catch (error) {
      toast.error(getError(error));
      dispatch({ type: "UPDATE_FAIL" });
    }
  };
  return (
    <Container className="small-container py-4">
      <Helmet>
        <title>Edit User</title>
      </Helmet>
      <h1>Edit User</h1>
      <p className="text-muted">ID: {userId}</p>

      {loading ? (
        <LoadingBox></LoadingBox>
      ) : error ? (
        <MessageBox variant="danger">{error}</MessageBox>
      ) : (
        <Form onSubmit={submitHandler} className="card p-4 shadow-sm border-0">
          <Form.Group className="mb-3" controlId="name">
            <Form.Label>Full Name</Form.Label>
            <Form.Control
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="email">
            <Form.Label>Email Address</Form.Label>
            <Form.Control
              value={email}
              type="email"
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="businessType">
            <Form.Label>User Type / Business Role</Form.Label>
            <Form.Select
              value={businessType}
              onChange={(e) => setBusinessType(e.target.value)}
            >
              <option value="User">User</option>
              <option value="Hotel">Hotel</option>
              <option value="Restaurant">Restaurant</option>
              <option value="Lounge">Lounge</option>
              <option value="Guest House">Guest House</option>
            </Form.Select>
          </Form.Group>

          <h5 className="mt-4 mb-3">Roles & Permissions</h5>
          <Form.Group className="mb-3" controlId="role">
            <Form.Label>Role</Form.Label>
            <Form.Select
              value={isAdmin ? 'Admin' : isSeller ? 'Manager' : isWaiter ? 'Waiter' : 'User'}
              onChange={(e) => {
                const role = e.target.value;
                setIsAdmin(role === 'Admin');
                setIsSeller(role === 'Manager');
                setIsWaiter(role === 'Waiter');
              }}
            >
              <option value="User">User</option>
              <option value="Admin">Admin (Full Access)</option>
              <option value="Manager">Manager (Dashboard Access)</option>
              <option value="Waiter">Waiter (Order Access)</option>
            </Form.Select>
          </Form.Group>

          <div className="d-grid mt-4">
            <Button disabled={loadingUpdate} type="submit" variant="primary">
              {loadingUpdate ? 'Updating...' : 'Update User'}
            </Button>
          </div>
        </Form>
      )}
    </Container>
  );
}
